import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { CreateFieldRequest, EditFieldRequest } from "~/types/api";

export const useWorkflowFieldMutations = (workflowId: number) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Field", {
        params: { path: { workflowId } },
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
    });
  };

  const createFieldMutation = useMutation({
    mutationFn: async (field: CreateFieldRequest) => {
      const response = await configurationApi.client.POST("/api/workflow/{workflowId}/Field", {
        params: { path: { workflowId } },
        body: field,
      });

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to create field");
        throw new Error(error);
      }

      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: () => {
      showToast("success", "Field created successfully");
      invalidateQueries();
    },
    onError: (error: Error) => {
      showToast("error", error.message, "Failed to create field");
    },
  });

  const updateFieldMutation = useMutation({
    mutationFn: async ({ fieldId, field }: { fieldId: number; field: EditFieldRequest }) => {
      const response = await configurationApi.client.PUT(
        "/api/workflow/{workflowId}/Field/{fieldId}",
        {
          params: { path: { workflowId, fieldId } },
          body: field,
        }
      );
      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to update field");
        throw new Error(error);
      }
      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: () => {
      showToast("success", "Field updated successfully");
      invalidateQueries();
    },
    onError: (error: Error) => {
      showToast("error", error.message, "Failed to update field");
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: async (fieldId: number) => {
      const response = await configurationApi.client.DELETE(
        "/api/workflow/{workflowId}/Field/{fieldId}",
        {
          params: { path: { workflowId, fieldId } },
        }
      );

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to delete field");
        throw new Error(error);
      }

      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: () => {
      showToast("success", "Field deleted successfully");
      invalidateQueries();
    },
    onError: (error: Error) => {
      showToast("error", error.message, "Failed to delete field");
    },
  });

  return {
    createField: createFieldMutation.mutateAsync,
    updateField: updateFieldMutation.mutateAsync,
    deleteField: deleteFieldMutation.mutateAsync,
    isCreating: createFieldMutation.isPending,
    isUpdating: updateFieldMutation.isPending,
    isDeleting: deleteFieldMutation.isPending,
  };
};
